import React, { useState } from 'react';
import './SiretLookup.css';

const NATURE_JURIDIQUE = {
  '1000': 'Entrepreneur individuel',
  '5410': 'SARL',
  '5499': 'SARL unipersonnelle',
  '5505': 'SA a conseil d\'administration',
  '5510': 'SA a directoire',
  '5599': 'SA',
  '5710': 'SAS',
  '5720': 'SASU',
  '5800': 'Societe europeenne',
  '6100': 'Caisse d\'epargne',
  '6210': 'GEIE',
  '6220': 'GIE',
  '6316': 'CUMA',
  '6317': 'Cooperative agricole',
  '6411': 'Societe d\'assurance mutuelle',
  '6521': 'SCOP SARL',
  '6532': 'SCOP SA',
  '6533': 'SCOP SAS',
  '6540': 'SEM',
  '6599': 'Societe cooperative commerciale',
  '7112': 'Autorite administrative independante',
  '7120': 'Ministere',
  '7150': 'Service du ministere',
  '7160': 'Service departement ou TOM',
  '7210': 'Commune',
  '7220': 'Departement',
  '7230': 'Region',
  '7312': 'Commune associee',
  '7344': 'SIVOM',
  '7346': 'Communaute de communes',
  '7348': 'Communaute d\'agglomeration',
  '7354': 'Metropole',
  '7389': 'Etablissement intercommunal',
  '7410': 'Centre hospitalier',
  '7430': 'Hopital',
  '7490': 'Etablissement public de sante',
  '8110': 'Regime general secu',
  '8210': 'Mutuelle',
  '8510': 'Regime agricole',
  '9110': 'Syndicat de proprietaires',
  '9150': 'Association declaree',
  '9210': 'Association simple',
  '9220': 'Association declaree reconnue UP',
  '9221': 'Association intermediaire',
  '9224': 'Association d\'insertion',
  '9230': 'Association declaree mission UP',
  '9240': 'Congregation',
  '9260': 'Association de droit local',
  '9300': 'Fondation'
};

const TRANCHE_EFFECTIF = {
  'NN': 'Non renseigne',
  '00': '0 salarie',
  '01': '1 ou 2 salaries',
  '02': '3 a 5 salaries',
  '03': '6 a 9 salaries',
  '11': '10 a 19 salaries',
  '12': '20 a 49 salaries',
  '21': '50 a 99 salaries',
  '22': '100 a 199 salaries',
  '31': '200 a 249 salaries',
  '32': '250 a 499 salaries',
  '41': '500 a 999 salaries',
  '42': '1 000 a 1 999 salaries',
  '51': '2 000 a 4 999 salaries',
  '52': '5 000 a 9 999 salaries',
  '53': '10 000 salaries et plus'
};

const CATEGORIE_ENTREPRISE = {
  'PME': 'PME',
  'ETI': 'ETI (Entreprise de taille intermediaire)',
  'GE': 'Grande entreprise'
};

const SECTION_ACTIVITE = {
  'A': 'Agriculture, sylviculture et peche',
  'B': 'Industries extractives',
  'C': 'Industrie manufacturiere',
  'D': 'Electricite, gaz, vapeur',
  'E': 'Eau, assainissement, dechets',
  'F': 'Construction',
  'G': 'Commerce, reparation auto',
  'H': 'Transports et entreposage',
  'I': 'Hebergement et restauration',
  'J': 'Information et communication',
  'K': 'Activites financieres et assurance',
  'L': 'Activites immobilieres',
  'M': 'Activites scientifiques et techniques',
  'N': 'Activites de services administratifs',
  'O': 'Administration publique',
  'P': 'Enseignement',
  'Q': 'Sante humaine et action sociale',
  'R': 'Arts, spectacles et activites recreatives',
  'S': 'Autres activites de services',
  'T': 'Activites des menages',
  'U': 'Activites extra-territoriales'
};

function formatCurrency(val) {
  if (!val && val !== 0) return '-';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val);
}

function SiretLookup({ onCompanyFound, initialSiret, compact }) {
  const [siretInput, setSiretInput] = useState(initialSiret || '');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [fullData, setFullData] = useState(null);
  const [error, setError] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  const cleanSiret = (val) => val.replace(/\s/g, '');

  const handleSearch = async () => {
    const cleaned = cleanSiret(siretInput);
    if (!cleaned || cleaned.length < 9) {
      setError('Entrez un SIRET (14 chiffres) ou SIREN (9 chiffres)');
      return;
    }

    setSearching(true);
    setError('');
    setResult(null);
    setFullData(null);
    setShowDetails(true);

    try {
      const response = await fetch(
        `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(cleaned)}&page=1&per_page=5`
      );

      if (!response.ok) {
        throw new Error('Erreur API');
      }

      const data = await response.json();

      if (!data.results || data.results.length === 0) {
        setError('Aucune entreprise trouvee pour ce numero');
        return;
      }

      let company = data.results.find(r =>
        r.siege?.siret === cleaned || r.siren === cleaned
      );
      if (!company) {
        company = data.results[0];
      }

      const siege = company.siege || {};
      const comps = company.complements || {};

      // Extraire tous les dirigeants
      const dirigeants = (company.dirigeants || []).map(dir => {
        if (dir.type_dirigeant === 'personne physique') {
          const prenom = dir.prenoms ? dir.prenoms.split(' ')[0] : '';
          const prenomF = prenom ? prenom.charAt(0).toUpperCase() + prenom.slice(1).toLowerCase() : '';
          const nomF = dir.nom ? dir.nom.charAt(0).toUpperCase() + dir.nom.slice(1).toLowerCase() : '';
          return {
            nom_complet: [prenomF, nomF].filter(Boolean).join(' '),
            qualite: dir.qualite || '',
            type: 'Personne physique',
            annee_naissance: dir.annee_de_naissance || '',
            nationalite: dir.nationalite || ''
          };
        }
        return {
          nom_complet: dir.denomination || dir.nom || '',
          qualite: dir.qualite || '',
          type: 'Personne morale',
          siren: dir.siren || ''
        };
      });

      const dirigeant_nom = dirigeants.length > 0 ? dirigeants[0].nom_complet : '';
      const dirigeant_qualite = dirigeants.length > 0 ? dirigeants[0].qualite : '';

      // Complements actifs
      const complementsActifs = [];
      if (comps.est_ess) complementsActifs.push('Economie Sociale et Solidaire');
      if (comps.est_association) complementsActifs.push('Association');
      if (comps.est_bio) complementsActifs.push('Certifie Bio');
      if (comps.est_rge) complementsActifs.push('RGE (Reconnu Garant Environnement)');
      if (comps.est_qualiopi) complementsActifs.push('Qualiopi');
      if (comps.est_organisme_formation) complementsActifs.push('Organisme de formation');
      if (comps.est_entrepreneur_spectacle) complementsActifs.push('Entrepreneur de spectacle');
      if (comps.est_finess) complementsActifs.push('FINESS (etablissement de sante)');
      if (comps.est_service_public) complementsActifs.push('Service public');
      if (comps.est_societe_mission) complementsActifs.push('Societe a mission');
      if (comps.est_siae) complementsActifs.push('SIAE (Insertion par l\'activite economique)');
      if (comps.convention_collective_renseignee) complementsActifs.push('Convention collective renseignee');
      if (comps.egapro_renseignee) complementsActifs.push('Index egalite pro renseigne');

      // Finances
      const finances = company.finances || {};
      const anneesFinances = Object.keys(finances).sort().reverse();

      const companyData = {
        nom: company.nom_complet || company.nom_raison_sociale || '',
        entreprise: company.nom_complet || company.nom_raison_sociale || '',
        adresse: siege.adresse || '',
        numero_siret: siege.siret || '',
        activite: company.activite_principale || '',
        ville: siege.libelle_commune || '',
        code_postal: siege.code_postal || '',
        dirigeant_nom,
        dirigeant_qualite
      };

      setResult(companyData);
      setFullData({
        // Identite
        siren: company.siren || '',
        siret: siege.siret || '',
        nom_complet: company.nom_complet || '',
        nom_raison_sociale: company.nom_raison_sociale || '',
        sigle: company.sigle || '',
        nature_juridique: company.nature_juridique || '',
        nature_juridique_label: NATURE_JURIDIQUE[company.nature_juridique] || company.nature_juridique || '',
        etat_administratif: company.etat_administratif === 'A' ? 'Actif' : company.etat_administratif === 'F' ? 'Ferme' : (company.etat_administratif || ''),
        date_creation: company.date_creation || '',
        date_fermeture: company.date_fermeture || '',
        date_mise_a_jour: company.date_mise_a_jour || '',
        // Activite
        activite_principale: company.activite_principale || '',
        section_activite: SECTION_ACTIVITE[company.section_activite_principale] || '',
        categorie_entreprise: CATEGORIE_ENTREPRISE[company.categorie_entreprise] || company.categorie_entreprise || '',
        tranche_effectif: TRANCHE_EFFECTIF[company.tranche_effectif_salarie] || company.tranche_effectif_salarie || '',
        annee_tranche_effectif: company.annee_tranche_effectif_salarie || '',
        caractere_employeur: company.caractere_employeur === 'O' ? 'Oui' : company.caractere_employeur === 'N' ? 'Non' : '',
        // Siege
        adresse: siege.adresse || '',
        numero_voie: siege.numero_voie || '',
        type_voie: siege.type_voie || '',
        libelle_voie: siege.libelle_voie || '',
        complement_adresse: siege.complement_adresse || '',
        code_postal: siege.code_postal || '',
        libelle_commune: siege.libelle_commune || '',
        departement: siege.departement || '',
        region: siege.region || '',
        latitude: siege.latitude || '',
        longitude: siege.longitude || '',
        nom_commercial: siege.nom_commercial || '',
        // Dirigeants
        dirigeants,
        // Etablissements
        nombre_etablissements: company.nombre_etablissements || 0,
        nombre_etablissements_ouverts: company.nombre_etablissements_ouverts || 0,
        // Complements
        complementsActifs,
        liste_idcc: comps.liste_idcc || [],
        identifiant_association: comps.identifiant_association || '',
        // Finances
        finances,
        anneesFinances
      });

      if (onCompanyFound) {
        onCompanyFound(companyData);
      }
    } catch (err) {
      console.error('Erreur recherche SIRET:', err);
      setError('Erreur lors de la recherche. Verifiez le numero et reessayez.');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className={`siret-lookup ${compact ? 'siret-lookup-compact' : ''}`}>
      <div className="siret-lookup-input-row">
        <div className="siret-lookup-field">
          <input
            type="text"
            value={siretInput}
            onChange={e => setSiretInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="N° SIRET ou SIREN"
            maxLength="17"
          />
        </div>
        <button
          type="button"
          className="siret-lookup-btn"
          onClick={handleSearch}
          disabled={searching || !siretInput.trim()}
        >
          {searching ? '...' : 'Rechercher'}
        </button>
      </div>

      {error && (
        <div className="siret-lookup-error">{error}</div>
      )}

      {result && fullData && (
        <div className="siret-lookup-result">
          <div className="siret-lookup-result-header">
            <div>
              <div className="siret-lookup-result-check">
                {fullData.etat_administratif === 'Actif' ? 'Entreprise active' : 'Entreprise fermee'}
              </div>
              <div className="siret-lookup-result-name">{result.nom}</div>
              {fullData.sigle && <div className="siret-lookup-result-sigle">({fullData.sigle})</div>}
            </div>
            <button
              type="button"
              className="siret-lookup-toggle"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? 'Masquer' : 'Voir tout'}
            </button>
          </div>

          {/* Resume toujours visible */}
          <div className="siret-lookup-summary">
            <div className="siret-lookup-summary-row">
              <span className="sl-label">SIRET</span>
              <span className="sl-value">{fullData.siret}</span>
            </div>
            <div className="siret-lookup-summary-row">
              <span className="sl-label">Adresse</span>
              <span className="sl-value">{fullData.adresse}</span>
            </div>
            <div className="siret-lookup-summary-row">
              <span className="sl-label">Activite</span>
              <span className="sl-value">{fullData.activite_principale}{fullData.section_activite ? ` — ${fullData.section_activite}` : ''}</span>
            </div>
            {fullData.dirigeants.length > 0 && (
              <div className="siret-lookup-summary-row">
                <span className="sl-label">Dirigeant</span>
                <span className="sl-value">
                  <strong>{fullData.dirigeants[0].nom_complet}</strong>
                  {fullData.dirigeants[0].qualite && ` (${fullData.dirigeants[0].qualite})`}
                </span>
              </div>
            )}
          </div>

          {/* Details complets */}
          {showDetails && (
            <div className="siret-lookup-details">

              {/* Identite */}
              <div className="sl-section">
                <div className="sl-section-title">Identite</div>
                <div className="sl-grid">
                  <div className="sl-row"><span className="sl-label">SIREN</span><span className="sl-value">{fullData.siren}</span></div>
                  <div className="sl-row"><span className="sl-label">SIRET (siege)</span><span className="sl-value">{fullData.siret}</span></div>
                  <div className="sl-row"><span className="sl-label">Raison sociale</span><span className="sl-value">{fullData.nom_raison_sociale}</span></div>
                  {fullData.nom_commercial && <div className="sl-row"><span className="sl-label">Nom commercial</span><span className="sl-value">{fullData.nom_commercial}</span></div>}
                  {fullData.sigle && <div className="sl-row"><span className="sl-label">Sigle</span><span className="sl-value">{fullData.sigle}</span></div>}
                  <div className="sl-row"><span className="sl-label">Forme juridique</span><span className="sl-value">{fullData.nature_juridique_label} ({fullData.nature_juridique})</span></div>
                  <div className="sl-row"><span className="sl-label">Etat</span><span className={`sl-value ${fullData.etat_administratif === 'Actif' ? 'sl-active' : 'sl-closed'}`}>{fullData.etat_administratif}</span></div>
                  <div className="sl-row"><span className="sl-label">Date de creation</span><span className="sl-value">{fullData.date_creation}</span></div>
                  {fullData.date_fermeture && <div className="sl-row"><span className="sl-label">Date de fermeture</span><span className="sl-value sl-closed">{fullData.date_fermeture}</span></div>}
                  {fullData.date_mise_a_jour && <div className="sl-row"><span className="sl-label">Derniere MAJ</span><span className="sl-value">{new Date(fullData.date_mise_a_jour).toLocaleDateString('fr-FR')}</span></div>}
                </div>
              </div>

              {/* Activite */}
              <div className="sl-section">
                <div className="sl-section-title">Activite & Effectifs</div>
                <div className="sl-grid">
                  <div className="sl-row"><span className="sl-label">Code NAF</span><span className="sl-value">{fullData.activite_principale}</span></div>
                  {fullData.section_activite && <div className="sl-row"><span className="sl-label">Secteur</span><span className="sl-value">{fullData.section_activite}</span></div>}
                  {fullData.categorie_entreprise && <div className="sl-row"><span className="sl-label">Categorie</span><span className="sl-value">{fullData.categorie_entreprise}</span></div>}
                  <div className="sl-row"><span className="sl-label">Effectif</span><span className="sl-value">{fullData.tranche_effectif}{fullData.annee_tranche_effectif ? ` (${fullData.annee_tranche_effectif})` : ''}</span></div>
                  {fullData.caractere_employeur && <div className="sl-row"><span className="sl-label">Employeur</span><span className="sl-value">{fullData.caractere_employeur}</span></div>}
                  <div className="sl-row"><span className="sl-label">Etablissements</span><span className="sl-value">{fullData.nombre_etablissements_ouverts} ouvert{fullData.nombre_etablissements_ouverts > 1 ? 's' : ''} / {fullData.nombre_etablissements} total</span></div>
                </div>
              </div>

              {/* Siege */}
              <div className="sl-section">
                <div className="sl-section-title">Siege social</div>
                <div className="sl-grid">
                  <div className="sl-row"><span className="sl-label">Adresse</span><span className="sl-value">{fullData.adresse}</span></div>
                  {fullData.complement_adresse && <div className="sl-row"><span className="sl-label">Complement</span><span className="sl-value">{fullData.complement_adresse}</span></div>}
                  <div className="sl-row"><span className="sl-label">Code postal</span><span className="sl-value">{fullData.code_postal}</span></div>
                  <div className="sl-row"><span className="sl-label">Commune</span><span className="sl-value">{fullData.libelle_commune}</span></div>
                  <div className="sl-row"><span className="sl-label">Departement</span><span className="sl-value">{fullData.departement}</span></div>
                  <div className="sl-row"><span className="sl-label">Region</span><span className="sl-value">{fullData.region}</span></div>
                  {fullData.latitude && fullData.longitude && (
                    <div className="sl-row"><span className="sl-label">Coordonnees GPS</span><span className="sl-value">{fullData.latitude}, {fullData.longitude}</span></div>
                  )}
                </div>
              </div>

              {/* Dirigeants */}
              {fullData.dirigeants.length > 0 && (
                <div className="sl-section">
                  <div className="sl-section-title">Dirigeant{fullData.dirigeants.length > 1 ? 's' : ''} ({fullData.dirigeants.length})</div>
                  <div className="sl-dirigeants-list">
                    {fullData.dirigeants.map((dir, i) => (
                      <div key={i} className="sl-dirigeant-card">
                        <div className="sl-dirigeant-name">{dir.nom_complet}</div>
                        <div className="sl-dirigeant-info">
                          {dir.qualite && <span className="sl-dirigeant-qualite">{dir.qualite}</span>}
                          {dir.type && <span className="sl-dirigeant-type">{dir.type}</span>}
                          {dir.annee_naissance && <span>Naissance: {dir.annee_naissance}</span>}
                          {dir.nationalite && <span>Nationalite: {dir.nationalite}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Finances */}
              {fullData.anneesFinances.length > 0 && (
                <div className="sl-section">
                  <div className="sl-section-title">Finances</div>
                  <div className="sl-finances-table">
                    <div className="sl-finances-header">
                      <span>Annee</span>
                      <span>Chiffre d'affaires</span>
                      <span>Resultat net</span>
                    </div>
                    {fullData.anneesFinances.map(annee => (
                      <div key={annee} className="sl-finances-row">
                        <span className="sl-finances-year">{annee}</span>
                        <span>{formatCurrency(fullData.finances[annee]?.ca)}</span>
                        <span className={fullData.finances[annee]?.resultat_net < 0 ? 'sl-negative' : ''}>{formatCurrency(fullData.finances[annee]?.resultat_net)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Complements */}
              {(fullData.complementsActifs.length > 0 || fullData.identifiant_association || fullData.liste_idcc.length > 0) && (
                <div className="sl-section">
                  <div className="sl-section-title">Complements & Labels</div>
                  <div className="sl-complements">
                    {fullData.complementsActifs.map((c, i) => (
                      <span key={i} className="sl-complement-badge">{c}</span>
                    ))}
                    {fullData.identifiant_association && (
                      <div className="sl-row" style={{ marginTop: '8px' }}><span className="sl-label">N° association</span><span className="sl-value">{fullData.identifiant_association}</span></div>
                    )}
                    {fullData.liste_idcc.length > 0 && (
                      <div className="sl-row" style={{ marginTop: '4px' }}><span className="sl-label">IDCC</span><span className="sl-value">{fullData.liste_idcc.join(', ')}</span></div>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SiretLookup;
